/**
 * 관리자 작업현황 대시보드 React Query 훅.
 *
 * - ``useWorkStats(range)`` — 기간별 작업자 net 집계. range 가 바뀌면 재요청.
 * - ``useUserRequests(userId)`` / ``useUserJobs(userId)`` — 드릴다운(읽기전용).
 *   ``enabled`` 로 행을 펼쳤을 때만 fetch 하도록 호출부에서 제어한다.
 */

import { useQuery } from "@tanstack/react-query";

import {
  listUserJobs,
  listUserRequests,
  listWorkStats,
  type UserWorkStatOut,
  type WorkStatsRange,
} from "@/lib/api/admin_work_stats";
import type { JobOut } from "@/lib/api/jobs";
import type { RequestOut } from "@/lib/api/requests";

export const WORK_STATS_QUERY_KEY = ["admin", "work-stats"] as const;

export function useWorkStats(range: WorkStatsRange) {
  return useQuery<UserWorkStatOut[]>({
    queryKey: [...WORK_STATS_QUERY_KEY, range.from ?? "", range.to ?? ""],
    queryFn: () => listWorkStats(range),
    staleTime: 30_000,
  });
}

export function useUserRequests(userId: number | null, enabled: boolean) {
  return useQuery<RequestOut[]>({
    queryKey: ["admin", "user-requests", userId],
    queryFn: () => listUserRequests(userId as number),
    enabled: enabled && userId != null,
    staleTime: 30_000,
  });
}

export function useUserJobs(userId: number | null, enabled: boolean) {
  return useQuery<JobOut[]>({
    queryKey: ["admin", "user-jobs", userId],
    queryFn: () => listUserJobs(userId as number),
    enabled: enabled && userId != null,
    staleTime: 30_000,
  });
}
